/**
 * Hook genérico de auto-save debounced. Recebe um valor e um callback
 * de persistência; dispara o callback depois de `delay` ms sem
 * mudança. Se o valor muda antes, reinicia o timer (debounce clássico).
 *
 * Expõe um `state` para a UI mostrar feedback:
 *   - `'idle'`    — valor igual ao último salvo
 *   - `'pending'` — timer ativo aguardando debounce
 *   - `'saving'`  — callback em andamento
 *   - `'saved'`   — último save concluído com sucesso
 *   - `'error'`   — callback rejeitou
 *
 * **Por que genérico** (não acoplado a `submit-onboarding-answer`): o
 * auto-save do subsistema de Forms vai valer para **qualquer** form
 * (pesquisa com clínica, satisfação de paciente, etc. — M1-01d-iii).
 * Mantém o hook em `features/forms/renderer/hooks/` para ser reusado
 * por qualquer consumer.
 *
 * **Não salva valor igual ao inicial**: se o user abre a tela e fecha
 * sem mexer, nenhum autosave dispara (compara via JSON.stringify —
 * suficiente para payloads pequenos do form).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface UseAutosaveArgs<T> {
  value: T;
  /** Função que persiste `value`. Deve rejeitar em falha. */
  onSave: (value: T) => Promise<void>;
  /** Debounce em ms. Default 2000 (2s) — balanceado entre
   * responsividade e volume de requests. */
  delayMs?: number;
  /** Se true, skip do primeiro save (quando `value` é o valor
   * inicial carregado do servidor). Default `true`. */
  skipInitial?: boolean;
}

export interface UseAutosaveResult {
  state: AutosaveState;
  /** Último erro capturado. Limpa no próximo save bem-sucedido. */
  error: Error | null;
  /** Cancela qualquer autosave pendente (timer de debounce ativo) E
   * desliga autosaves futuros até a próxima mudança em `value` que
   * não seja igual ao último salvo. Use antes de um submit final pra
   * evitar que o debounce dispare DEPOIS do submit e bata num token
   * já submetido (bug 2026-04-24: Alexandre viu 409 `already_submitted`
   * em submit-onboarding-answer porque o autosave disparava 2-20s
   * depois do submit, batendo numa submission já fechada). */
  cancel: () => void;
}

export function useAutosave<T>({
  value,
  onSave,
  delayMs = 2000,
  skipInitial = true,
}: UseAutosaveArgs<T>): UseAutosaveResult {
  const [state, setState] = useState<AutosaveState>('idle');
  const [error, setError] = useState<Error | null>(null);
  const initialValueRef = useRef<string | null>(null);
  const lastSavedRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flag que o caller pode setar via `cancel()` pra parar o auto-save
  // em definitivo (ex: após submit final bem-sucedido). O useEffect
  // consulta esse ref antes de agendar novo timer. Não é reiniciado
  // automaticamente — se precisar reativar, remontar o componente.
  const stoppedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    stoppedRef.current = true;
    setState('idle');
  }, []);

  useEffect(() => {
    const serialized = JSON.stringify(value);

    // Captura o valor inicial no primeiro render.
    if (initialValueRef.current === null) {
      initialValueRef.current = serialized;
      lastSavedRef.current = serialized;
      return;
    }

    // Se `skipInitial` e o valor voltou ao inicial, trata como não-dirty.
    if (skipInitial && serialized === lastSavedRef.current) {
      return;
    }

    // Caller chamou `cancel()` — não agenda nem dispara nada até o
    // componente remontar. Útil depois de um submit final.
    if (stoppedRef.current) {
      return;
    }

    setState('pending');
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      // Re-checa antes de executar: o caller pode ter chamado cancel()
      // enquanto o timer estava pendente.
      if (stoppedRef.current) {
        setState('idle');
        return;
      }
      setState('saving');
      onSave(value)
        .then(() => {
          lastSavedRef.current = serialized;
          setState('saved');
          setError(null);
        })
        .catch((err) => {
          setState('error');
          setError(err instanceof Error ? err : new Error(String(err)));
        });
    }, delayMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, onSave, delayMs, skipInitial]);

  return { state, error, cancel };
}
